import { createFileRoute } from "@tanstack/react-router";
import type { RWTable } from "common/data/table";
import { AddGridConfigControls } from "lib/components/debug/framing/AddGridConfigControls";
import { AddGridLines } from "lib/components/debug/framing/AddGridLines";
import { AddNodeControls } from "lib/components/debug/framing/AddNodeControls";
import { AddNodeLoadControls } from "lib/components/debug/framing/AddNodeLoadControls";
import { AddReferenceAxes } from "lib/components/debug/framing/AddReferenceAxes";
import { AddRestraintControls } from "lib/components/debug/framing/AddRestraintControls";
import { AddTrussMemberControls } from "lib/components/debug/framing/AddTrussMemberControls";
import { AddZoomControls } from "lib/components/debug/framing/AddZoomControls";
import { MIN_ZOOM, MAX_ZOOM } from "lib/components/debug/framing/Constants";
import { DeformedElementsOverlay } from "lib/components/debug/framing/DeformedElementsOverlay";
import { DisplayElements } from "lib/components/debug/framing/DisplayElements";
import { DisplayNodeLoads } from "lib/components/debug/framing/DisplayNodeLoads";
import { DisplayNodes } from "lib/components/debug/framing/DisplayNodes";
import { DisplayRestraints } from "lib/components/debug/framing/DisplayRestraints";
import { MemberForcesOverlay } from "lib/components/debug/framing/MemberForcesOverlay";
import { NodeLoadsTable } from "lib/components/debug/framing/NodeLoadsTable";
import { NodesTable } from "lib/components/debug/framing/NodesTable";
import { RestraintReactionsOverlay } from "lib/components/debug/framing/RestraintReactionsOverlay";
import { RestraintsTable } from "lib/components/debug/framing/RestraintsTable";
import { StructuralAnalysisControls } from "lib/components/debug/framing/StructuralAnalysisControls";
import { TrussMembersTable } from "lib/components/debug/framing/TrussMembersTable";
import type { Point2D, GridConfig, Restraint, NodeLoad2D, Element } from "lib/components/debug/framing/types";
import { useStructuralModeler } from "lib/components/debug/framing/useStructuralModeler";
import { computeGridLines } from "lib/components/debug/framing/utils";
import type { Node2D, Restraint2D, Truss2D } from "lib/components/structuralAnalysis/structural2DTypes";
import { ElementType2D } from "lib/components/structuralAnalysis/structural2DTypes";
import { solveTruss2D } from "lib/components/structuralAnalysis/truss2D/truss2D";
import { useState, useRef, useEffect } from "react";

type Line2D = [number, number];

const defaultGridConfig: GridConfig = {
  xStep: 50,
  yStep: 50,
};

function Truss2DPage() {
  const [gridConfig, setGridConfig] = useState<GridConfig>(defaultGridConfig);
  const [points, setPoints] = useState<Point2D[]>([]);
  const [lines, setLines] = useState<Element[]>([]);
  const [svgSize, setSvgSize] = useState<{ width: number; height: number }>({ width: 800, height: 600 });
  const [zoom, setZoom] = useState<number>(1);
  const [lineStartIdx, setLineStartIdx] = useState<number>(0);
  const [lineEndIdx, setLineEndIdx] = useState<number>(0);
  const [lineMode, setLineMode] = useState<boolean>(false);
  const [pendingLine, setPendingLine] = useState<number | null>(null);
  const [pointMode, setPointMode] = useState<boolean>(false);
  const [restraints, setRestraints] = useState<Restraint[]>([]);
  const [restraintMode, setRestraintMode] = useState<boolean>(false);
  const [restraintTx, setRestraintTx] = useState<boolean>(true);
  const [restraintTy, setRestraintTy] = useState<boolean>(false);
  const [restraintJointIdx, setRestraintJointIdx] = useState<number>(0);
  const [nodeLoads, setNodeLoads] = useState<NodeLoad2D[]>([]);
  const [loadNodeIdx, setLoadNodeIdx] = useState<number>(0);
  const [loadFx, setLoadFx] = useState<number>(1);
  const [loadFy, setLoadFy] = useState<number>(0);
  const [loadMode, setLoadMode] = useState<boolean>(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const MIN_PANEL_WIDTH = 320;
  const MAX_PANEL_WIDTH = MIN_PANEL_WIDTH * 2;
  const [panelWidth, setPanelWidth] = useState<number>(MIN_PANEL_WIDTH);
  const [resizing, setResizing] = useState<boolean>(false);
  const [analysisResult, setAnalysisResult] = useState<any | null>(null);
  const [showDeformation, setShowDeformation] = useState<boolean>(true);
  const [showMemberForces, setShowMemberForces] = useState<boolean>(false);
  const [showRestraintReactions, setShowRestraintReactions] = useState<boolean>(false);
  const [deformationScale, setDeformationScale] = useState<number>(0.1);

  // Reset analysisResult if the model changes
  useEffect(() => {
    setAnalysisResult(null);
  }, [points, lines, nodeLoads, restraints]);

  // Handle mouse move for resizing
  useEffect(() => {
    if (!resizing) {
      return;
    }
    const handleMouseMove = (e: MouseEvent) => {
      setPanelWidth(() => Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, e.clientX)));
    };
    const handleMouseUp = () => setResizing(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing]);

  // Measure SVG size on mount and when window resizes or panelWidth changes
  useEffect(() => {
    function updateSize() {
      // Use the available space for the SVG viewer
      setSvgSize({
        width: window.innerWidth - panelWidth,
        height: window.innerHeight,
      });
    }
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [panelWidth]);

  // Compute grid lines based on svgSize and spacing, adjusted for zoom
  const viewWidth = svgSize.width / zoom;
  const viewHeight = svgSize.height / zoom;

  const gridOriginX = viewWidth / 2;
  const gridOriginY = viewHeight / 2;

  const {
    handleZoomIn,
    handleZoomOut,
    handleResetView,
    handleSvgClick,
    handleSvgContextMenu,
    handleAddLine,
    handleAddRestraint,
    handleAddNodeLoad,
    handleDeleteNodeLoad,
    handleClearNodeLoads,
  } = useStructuralModeler({
    zoom,
    setZoom,
    svgRef,
    gridConfig,
    pointMode,
    setPointMode,
    lineMode,
    setLineMode,
    pendingLine,
    setPendingLine,
    points,
    setPoints,
    setLines,
    gridOriginX,
    gridOriginY,
    setRestraints,
    restraintMode,
    setRestraintMode,
    restraintTx,
    restraintTy,
    restraintJointIdx,
    setRestraintJointIdx,
    setNodeLoads,
    loadNodeIdx,
    setLoadNodeIdx,
    loadFx,
    loadFy,
    loadMode,
    setLoadMode,
  });

  // Compute grid lines symmetrically around the origin
  const halfGridWidth = viewWidth / 2;
  const halfGridHeight = viewHeight / 2;
  const { xLines, yLines } = computeGridLines(
    gridOriginX,
    gridOriginY,
    halfGridWidth,
    halfGridHeight,
    gridConfig.xStep,
    gridConfig.yStep,
  );

  // Compute SVG cursor style
  const svgCursor = pointMode || lineMode || restraintMode || loadMode ? "crosshair" : "pointer";

  // Snap tolerance is 15% of the minimum grid step, but at least 4 units
  const snapTolerance = Math.max(4, Math.min(gridConfig.xStep, gridConfig.yStep) * 0.15);

  // Alert and delete logic for joints
  const [, setJointDeleteAlert] = useState<string | null>(null);
  const jointDeleteTimeout = useRef<NodeJS.Timeout | null>(null);
  const handleDeleteJoint = (idx: number): void => {
    if (lines.some((l) => l.i0 === idx || l.i1 === idx)) {
      setJointDeleteAlert("Cannot delete joint: it is used by a truss member.");
      if (jointDeleteTimeout.current) {
        clearTimeout(jointDeleteTimeout.current);
      }
      jointDeleteTimeout.current = setTimeout(() => setJointDeleteAlert(null), 3500);
      return;
    }
    setPoints((prev) => prev.filter((_, i) => i !== idx));
    setLines((prev) =>
      prev
        .filter((l) => l.i0 !== idx && l.i1 !== idx)
        .map((l) => ({ ...l, i0: l.i0 > idx ? l.i0 - 1 : l.i0, i1: l.i1 > idx ? l.i1 - 1 : l.i1 })),
    );
    setRestraints((prev) =>
      prev.filter((r) => r.joint !== idx).map((r) => ({ ...r, joint: r.joint > idx ? r.joint - 1 : r.joint })),
    );
    setJointDeleteAlert(null);
  };

  const handleDeleteLine = (idx: number): void => {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteRestraint = (idx: number): void => {
    setRestraints((prev) => prev.filter((_, i) => i !== idx));
  };

  useEffect(() => {
    setLines((prev) => {
      if (prev.length > 0 && Array.isArray(prev[0]) && prev[0].length === 2) {
        // Old format, convert
        return (prev as any as Line2D[]).map(([i0, i1]) => ({ i0, i1, area: 1, e: 1000 }));
      }
      return prev;
    });
  }, []);

  // --- Structural Analysis Handler ---
  const handleRunStructuralAnalysis = (): void => {
    // NodesTable: { [id]: { nodeId, x, y } }
    const nodes: RWTable<Node2D> = points.reduce((acc, [x, y], i) => {
      acc[i + 1] = { id: i + 1, x, y };
      return acc;
    }, {} as RWTable<Node2D>);
    // ElementsTable: { [id]: { elementId, elementType, iNodeId, jNodeId, area } }
    const elements: RWTable<Truss2D> = lines.reduce((acc, l, i) => {
      acc[i + 1] = {
        id: i + 1,
        elementType: ElementType2D.TRUSS2D,
        startNodeId: l.i0 + 1,
        endNodeId: l.i1 + 1,
        area: l.area,
        modulusOfElasticity: l.e,
      };
      return acc;
    }, {} as RWTable<Truss2D>);
    // RestraintsTable: { [id]: { nodeId, tx, ty } }
    const restraintsTable: RWTable<Restraint2D> = restraints.reduce((acc, r, i) => {
      acc[i + 1] = { nodeId: r.joint + 1, tx: r.tx, ty: r.ty };
      return acc;
    }, {} as RWTable<Restraint2D>);
    // LoadsTable: { [id]: { nodeId, fx, fy } }
    const loads: RWTable<NodeLoad2D> = nodeLoads.reduce((acc, l, i) => {
      acc[i + 1] = { nodeId: l.nodeId + 1, fx: l.fx ?? 0, fy: l.fy ?? 0 };
      return acc;
    }, {} as RWTable<NodeLoad2D>);
    try {
      const result = solveTruss2D(nodes, restraintsTable, elements, loads);
      setAnalysisResult(result);
    } catch (err) {
      console.error("Structural analysis failed:", err);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        fontFamily: "Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif",
      }}
    >
      {/* Left Panel: UI Controls */}
      <div
        style={{
          width: panelWidth,
          minWidth: MIN_PANEL_WIDTH,
          maxWidth: MAX_PANEL_WIDTH,
          padding: 4,
          borderRight: "1px solid #eee",
          boxSizing: "border-box",
          height: "100vh",
          overflowY: "auto",
          fontFamily: "inherit",
        }}
      >
        <h2>TRUSS 2D ANALYSIS</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <AddGridConfigControls gridConfig={gridConfig} setGridConfig={setGridConfig} svgSize={svgSize} />
          <AddZoomControls
            zoom={zoom}
            handleZoomIn={handleZoomIn}
            handleZoomOut={handleZoomOut}
            handleResetView={handleResetView}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
          />
          <AddNodeControls
            gridConfig={gridConfig}
            snapTolerance={snapTolerance}
            pointMode={pointMode}
            setPointMode={setPointMode}
            setLineMode={setLineMode}
            setPendingLine={setPendingLine}
            setPoints={setPoints}
            setRestraintMode={setRestraintMode}
            points={points}
          />
          <NodesTable points={points} onDelete={handleDeleteJoint} setPoints={setPoints} />
          <div style={{ borderTop: "1px solid #e0e0e0", margin: "4px 0 0 0" }} />
          <AddTrussMemberControls
            points={points}
            lines={lines}
            lineStartIdx={lineStartIdx}
            setLineStartIdx={setLineStartIdx}
            lineEndIdx={lineEndIdx}
            setLineEndIdx={setLineEndIdx}
            handleAddLine={handleAddLine}
            lineMode={lineMode}
            pendingLine={pendingLine}
            setLineMode={setLineMode}
            setPendingLine={setPendingLine}
            setPointMode={setPointMode}
            setLines={setLines}
            setRestraintMode={setRestraintMode}
          />
          <TrussMembersTable lines={lines} onDelete={handleDeleteLine} points={points} setLines={setLines} />
          <div style={{ borderTop: "1px solid #e0e0e0", margin: "4px 0 0 0" }} />
          <AddRestraintControls
            restraints={restraints}
            setRestraints={setRestraints}
            restraintMode={restraintMode}
            setRestraintMode={setRestraintMode}
            restraintJointIdx={restraintJointIdx}
            setRestraintJointIdx={setRestraintJointIdx}
            restraintTx={restraintTx}
            setRestraintTx={setRestraintTx}
            restraintTy={restraintTy}
            setRestraintTy={setRestraintTy}
            points={points}
            handleAddRestraint={handleAddRestraint}
            setPointMode={setPointMode}
            setLineMode={setLineMode}
          />
          <div style={{ borderTop: "1px solid #e0e0e0", margin: "4px 0 0 0" }} />
          <RestraintsTable restraints={restraints} onDelete={handleDeleteRestraint} setRestraints={setRestraints} />
          <div style={{ borderTop: "1px solid #e0e0e0", margin: "4px 0 0 0" }} />
          <AddNodeLoadControls
            points={points}
            nodeLoads={nodeLoads}
            loadNodeIdx={loadNodeIdx}
            setLoadNodeIdx={setLoadNodeIdx}
            loadFx={loadFx}
            setLoadFx={setLoadFx}
            loadFy={loadFy}
            setLoadFy={setLoadFy}
            handleAddNodeLoad={handleAddNodeLoad}
            handleClearNodeLoads={handleClearNodeLoads}
            setLoadMode={setLoadMode}
            loadMode={loadMode}
            setRestraintMode={setRestraintMode}
          />
          <NodeLoadsTable nodeLoads={nodeLoads} onDelete={handleDeleteNodeLoad} setNodeLoads={setNodeLoads} />
          <div style={{ borderTop: "1px solid #e0e0e0", margin: "4px 0 0 0" }} />
          <StructuralAnalysisControls
            onRunAnalysis={handleRunStructuralAnalysis}
            showDeformation={showDeformation}
            setShowDeformation={setShowDeformation}
            deformationScale={deformationScale}
            setDeformationScale={setDeformationScale}
            showMemberForces={showMemberForces}
            setShowMemberForces={setShowMemberForces}
            showRestraintReactions={showRestraintReactions}
            setShowRestraintReactions={setShowRestraintReactions}
          />
        </div>
      </div>
      {/* Resizer - now between panels */}
      <div
        style={{
          width: 8,
          cursor: "col-resize",
          background: resizing ? "#bdbdbd" : "#e0e0e0",
          zIndex: 100,
          userSelect: "none",
          height: "100vh",
          boxShadow: "0 0 4px #bbb",
          transition: "background 0.2s",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onMouseDown={() => setResizing(true)}
        title="Drag to resize panel"
      >
        <div
          style={{
            width: 2,
            height: "60%",
            background: "#888",
            borderRadius: 2,
            opacity: 0.5,
          }}
        />
      </div>
      {/* Right Panel: SVG Viewer */}
      <div style={{ flex: 1, position: "relative", background: "#fafbfc", fontFamily: "inherit" }}>
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          viewBox={`0 0 ${viewWidth} ${viewHeight}`}
          style={{ display: "block", width: "100%", height: "100%", cursor: svgCursor }}
          onMouseDown={handleSvgClick}
          onContextMenu={handleSvgContextMenu}
        >
          {showMemberForces && analysisResult && analysisResult.forces && (
            <MemberForcesOverlay
              lines={lines}
              points={points}
              forces={analysisResult.forces}
              gridOriginX={gridOriginX}
              gridOriginY={gridOriginY}
              zoom={zoom}
            />
          )}
          {showRestraintReactions && analysisResult && analysisResult.reactions && (
            <RestraintReactionsOverlay
              points={points}
              reactions={analysisResult.reactions}
              gridOriginX={gridOriginX}
              gridOriginY={gridOriginY}
              zoom={zoom}
            />
          )}
          <AddReferenceAxes gridOriginX={gridOriginX} gridOriginY={gridOriginY} zoom={zoom} />
          <AddGridLines xLines={xLines} yLines={yLines} viewWidth={viewWidth} viewHeight={viewHeight} />
          <DisplayNodes points={points} gridOriginX={gridOriginX} gridOriginY={gridOriginY} zoom={zoom} />
          <DisplayElements
            lines={lines}
            points={points}
            gridOriginX={gridOriginX}
            gridOriginY={gridOriginY}
            zoom={zoom}
          />
          {/* Deformed shape overlay */}
          {showDeformation && analysisResult && analysisResult.displacements && (
            <DeformedElementsOverlay
              lines={lines}
              points={points}
              displacements={analysisResult.displacements}
              restraints={restraints}
              gridOriginX={gridOriginX}
              gridOriginY={gridOriginY}
              zoom={zoom}
              deformationScale={deformationScale}
            />
          )}
          <DisplayRestraints
            restraints={restraints}
            points={points}
            gridOriginX={gridOriginX}
            gridOriginY={gridOriginY}
            zoom={zoom}
          />
          <DisplayNodeLoads
            nodeLoads={nodeLoads}
            points={points}
            gridOriginX={gridOriginX}
            gridOriginY={gridOriginY}
            zoom={zoom}
          />
        </svg>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/debug/framing/truss2D")({
  component: Truss2DPage,
});
